import React from "react";
import { useTranslation } from "@shared/utils/internationalization";
import Flex from "@ui-kit/layout/Flex/Flex.web";
import Text from "@ui-kit/typography/Text/Text.web";

import ParsedHtml from "@ui-kit/typography/ParsedHtml/ParsedHtml.web";

const customTicketMessageCss = {
  "& p": {
    marginBottom: 0,
  },
};

export interface TicketsNotAvailableTextProps {
  ticketsNotRequired: boolean;
  pastEvent: boolean;
  soldOutEvent: boolean;
  waitlistedEvent: boolean;
  customTicketMessage: string;
}

const TicketsNotAvailableText: React.FC<TicketsNotAvailableTextProps> = ({
  ticketsNotRequired,
  pastEvent,
  soldOutEvent,
  waitlistedEvent,
  customTicketMessage,
}) => {
  const { t } = useTranslation("purchases");

  const InfoTextContainer: React.FC<{ inputText: string }> = ({
    inputText,
  }) => (
    <Flex width="100%" justifyContent="flex-start">
      <Text as="h2" fontSize={{ base: "md", sm: "xl" }} fontWeight="bold">
        {inputText}
      </Text>
    </Flex>
  );

  const getTextDisplay = (): JSX.Element => {
    // default: no display
    let displayElement = null;

    // if event is not over:
    if (!pastEvent) {
      // display not available text if tickets aren't required for the event
      if (ticketsNotRequired) {
        displayElement = (
          <InfoTextContainer
            inputText={t("Tickets are not required for this event.")}
          />
        );
      }
      // display not available text if not sold out
      if (!soldOutEvent && !ticketsNotRequired) {
        displayElement = (
          <InfoTextContainer
            inputText={t("Sorry, no tickets are available.")}
          />
        );
      }
      // sold out event
      if (soldOutEvent) {
        if (waitlistedEvent) {
          // display nothing for waitlists
          displayElement = <></>;
        } else {
          // display sold out text
          displayElement = (
            <InfoTextContainer
              inputText={t("Sorry, online tickets are sold out.")}
            />
          );
        }
      }
      // add custom html message below other displayed messages
      if (customTicketMessage) {
        displayElement = (
          <>
            {displayElement && (
              <Flex width="100%" mb={{ base: "lg", sm: "2xl" }}>
                {displayElement}
              </Flex>
            )}
            <Flex css={customTicketMessageCss}>
              <ParsedHtml htmlString={customTicketMessage} />
            </Flex>
          </>
        );
      }
    }
    // if event is over, display event ended text
    else {
      displayElement = (
        <InfoTextContainer inputText={t("Sorry, this event has ended.")} />
      );
    }
    return displayElement;
  };

  return getTextDisplay();
};

export default TicketsNotAvailableText;
